import React, { useEffect, useId, useRef, useState } from 'react';
import { NOVIDADES, NOVIDADES_STORAGE_KEY, NOVIDADES_VERSAO, NOVIDADES_VISIVEIS } from '../lib/novidades';
import { novidadesBannerEnabled } from '../lib/flags';

const ALTURA = '2.75rem';

function formatar(data) {
  const [a, m, d] = data.split('-');
  return `${d}/${m}/${a}`;
}

/**
 * Faixa fina fixa no rodapé da tela com "Ver novidades". Fechada, não volta até a versão mudar
 * (uma única chave no navegador: `novidades-visto`, só o texto da versão).
 * Nada de dados do cálculo; sem animação; escondida na impressão.
 */
export default function NovidadesBar() {
  const enabled = novidadesBannerEnabled();
  const [visivel, setVisivel] = useState(false); // começa fechada: o servidor não renderiza a faixa e nada se desloca
  const [aberta, setAberta] = useState(false);
  const [todas, setTodas] = useState(false);
  const painelId = useId();
  const tituloRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    let visto = null;
    try { visto = window.localStorage.getItem(NOVIDADES_STORAGE_KEY); } catch { /* sem armazenamento: a faixa aparece */ }
    setVisivel(visto !== NOVIDADES_VERSAO);
  }, [enabled]);

  useEffect(() => {
    if (!visivel) { document.documentElement.style.removeProperty('--novidades-h'); return undefined; }
    document.documentElement.style.setProperty('--novidades-h', ALTURA);
    return () => document.documentElement.style.removeProperty('--novidades-h');
  }, [visivel]);

  useEffect(() => {
    if (aberta) tituloRef.current?.focus({ preventScroll: true });
  }, [aberta]);

  useEffect(() => {
    if (!aberta) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setAberta(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [aberta]);

  const fechar = () => {
    try { window.localStorage.setItem(NOVIDADES_STORAGE_KEY, NOVIDADES_VERSAO); } catch { /* a faixa some só nesta página */ }
    setAberta(false);
    setVisivel(false);
    // o foco não se perde no <body>: vai para o conteúdo principal
    const main = document.querySelector('main');
    if (main) { if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1'); main.focus({ preventScroll: true }); }
  };

  if (!enabled || !visivel) return null;

  const itens = todas ? NOVIDADES : NOVIDADES.slice(0, NOVIDADES_VISIVEIS);

  return (
    <div className="no-print fixed inset-x-0 bottom-0 z-[45]" data-novidades>
      {aberta && (
        <section id={painelId} aria-label="Novidades" className="mx-auto mb-0 max-h-[60vh] max-w-[640px] overflow-y-auto rounded-t-[8px] border border-b-0 border-[var(--line)] bg-[var(--surface)] px-4 py-3">
          <h2 ref={tituloRef} tabIndex={-1} className="text-[length:var(--fs-16)] font-semibold text-[var(--ink)] outline-none">Novidades no MiniMental</h2>
          <ul className="mt-2">
            {itens.map((item) => (
              <li key={item.texto} className="border-b border-[var(--line)] py-2 text-[length:var(--fs-15)] text-[var(--ink)] last:border-b-0">
                {item.texto}
                <span className="ml-2 text-[length:var(--fs-13)] text-[var(--ink-2)]">{formatar(item.data)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-1 flex flex-wrap items-center gap-x-4">
            {!todas && NOVIDADES.length > NOVIDADES_VISIVEIS && (
              <button type="button" onClick={() => setTodas(true)} className="min-h-11 text-[length:var(--fs-15)] font-medium text-[var(--accent)] underline">Ver todas</button>
            )}
            <button type="button" onClick={fechar} className="min-h-11 text-[length:var(--fs-15)] font-medium text-[var(--ink)] underline">Fechar</button>
          </div>
        </section>
      )}
      <div className="border-t border-[var(--line)] bg-[var(--surface)]" style={{ minHeight: ALTURA }}>
        <div className="mx-auto flex max-w-[1180px] items-center justify-between gap-3 px-4 text-[length:var(--fs-14)] text-[var(--ink-2)]" style={{ minHeight: ALTURA }}>
          <span className="whitespace-nowrap">Há novidades.</span>
          <span className="flex items-center gap-1">
            <button type="button" aria-expanded={aberta} aria-controls={painelId} onClick={() => setAberta((v) => !v)} className="min-h-11 px-2 font-medium text-[var(--accent)] underline">Ver novidades</button>
            <button type="button" onClick={fechar} className="min-h-11 px-2 font-medium text-[var(--ink)] underline">Fechar</button>
          </span>
        </div>
      </div>
    </div>
  );
}
